import React, { useRef, useState, Suspense } from 'react';
import { Canvas, useLoader, useFrame } from '@react-three/fiber';
import { TextureLoader } from 'three';
import { Html, OrbitControls, Stars } from '@react-three/drei';
import * as THREE from 'three';
import { Frame } from '@react95/core';

const Book = () => {
  const frontTexture = useLoader(TextureLoader, '/images/Front.png');
  const backTexture = useLoader(TextureLoader, '/images/Back.png');
  const pageTexture = useLoader(TextureLoader, '/images/Page.png');
  
  // Load all polaroid textures
  const polaroid1 = useLoader(TextureLoader, '/images/RicePolaroid1.png');
  const polaroid2 = useLoader(TextureLoader, '/images/RicePolaroid2.png');
  const polaroid3 = useLoader(TextureLoader, '/images/RicePolaroid3.png');
  const polaroid4 = useLoader(TextureLoader, '/images/RicePolaroid4.png');
  const polaroid5 = useLoader(TextureLoader, '/images/RicePolaroid5.png');
  const polaroid6 = useLoader(TextureLoader, '/images/RicePolaroid6.png');
  const polaroid7 = useLoader(TextureLoader, '/images/RicePolaroid7.png');
  const polaroid8 = useLoader(TextureLoader, '/images/RicePolaroid8.png');
  const polaroid9 = useLoader(TextureLoader, '/images/RicePolaroid9.png');
  const polaroid10 = useLoader(TextureLoader, '/images/RicePolaroid10.png');
  const polaroid11 = useLoader(TextureLoader, '/images/RicePolaroid11.png');
  
  const bookRef = useRef();
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  
  const pages = [
    { content: "" },  // Empty content for first page (behind front cover)
    { content: "", texture: polaroid1 },
    { content: "", texture: polaroid2 },
    { content: "", texture: polaroid3 },
    { content: "", texture: polaroid4 },
    { content: "", texture: polaroid5 },
    { content: "", texture: polaroid6 },
    { content: "", texture: polaroid7 },
    { content: "", texture: polaroid8 },
    { content: "", texture: polaroid9 },
    { content: "", texture: polaroid10 },
    { content: "", texture: polaroid11 }
  ];

  const handleNextPage = () => {
    if (!isFlipping && currentPage < pages.length - 1) {
      setIsFlipping(true);
      setCurrentPage(prev => prev + 1);
      setTimeout(() => setIsFlipping(false), 500);
    }
  };

  const handlePrevPage = () => {
    if (!isFlipping && currentPage > 0) {
      setIsFlipping(true);
      setCurrentPage(prev => prev - 1);
      setTimeout(() => setIsFlipping(false), 500);
    }
  };

  useFrame((state) => {
    if (bookRef.current) {
      bookRef.current.rotation.y = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.1;
    }
  });

  return (
    <group ref={bookRef}>
      <group position={[0, 0, 0]} rotation={[0.2, 0, 0]}>
        {/* Front Cover */}
        {currentPage === 0 && (
          <mesh position={[0, 0, 0.1]} renderOrder={2}>
            <planeGeometry args={[4.2, 6.2]} />
            <meshStandardMaterial 
              map={frontTexture}
              side={THREE.DoubleSide}
              transparent={true}
              depthWrite={true}
              depthTest={true}
            />
          </mesh>
        )}

        {/* Pages */}
        {pages.map((page, index) => (
          <group 
            key={index} 
            position={[
              index < currentPage ? -3.8 : 0,
              0,
              0.05 - (index * 0.01)
            ]}
            rotation={[0, index < currentPage ? Math.PI : 0, 0]}
          >
            {/* Base page */}
            <mesh renderOrder={1}>
              <planeGeometry args={[4, 6]} />
              <meshStandardMaterial 
                map={pageTexture} 
                side={THREE.DoubleSide}
                transparent={true}
                depthWrite={true}
                depthTest={true}
              />
            </mesh>
            {index > 0 && (
              <>
                {/* Current page content */}
                {index === currentPage && (
                  <group>
                    <Html
                      transform
                      position={[0, 1.5, 0.001]}
                      style={{
                        fontSize: '20px',
                        color: 'black',
                        background: 'transparent',
                        padding: '20px',
                        textAlign: 'center',
                        width: '100%',
                        pointerEvents: 'none',
                        userSelect: 'none',
                        fontFamily: 'OMORI_GAME, monospace',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        display: index === currentPage ? 'block' : 'none'
                      }}
                    >
                      {page.content}
                    </Html>
                    <mesh 
                      position={[0, 0, 0.002]} 
                      renderOrder={2}
                    >
                      <planeGeometry args={[2, 2.4]} />
                      <meshStandardMaterial
                        map={page.texture}
                        side={THREE.FrontSide}
                        transparent={true}
                        depthWrite={false}
                        depthTest={true}
                      />
                    </mesh>
                  </group>
                )}

                {/* Previous page content (left side) */}
                {index === currentPage - 1 && (
                  <group rotation={[0, -Math.PI, 0]}>
                    <Html
                      transform
                      position={[0, 1.5, -0.001]}
                      style={{
                        fontSize: '20px',
                        color: 'black',
                        background: 'transparent',
                        padding: '20px',
                        textAlign: 'center',
                        width: '100%',
                        pointerEvents: 'none',
                        userSelect: 'none',
                        fontFamily: 'OMORI_GAME, monospace',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        display: index === currentPage - 1 ? 'block' : 'none'
                      }}
                    >
                      {page.content}
                    </Html>
                    <mesh 
                      position={[0, 0, -0.002]} 
                      renderOrder={999}
                    >
                      <planeGeometry args={[2, 2.4]} />
                      <meshStandardMaterial
                        map={page.texture}
                        side={THREE.FrontSide}
                        transparent={true}
                        depthWrite={false}
                        depthTest={false}
                      />
                    </mesh>
                  </group>
                )}
              </>
            )}
          </group>
        ))}

        {/* Back Cover */}
        <mesh position={[0, 0, 0.05 - ((pages.length + 1) * 0.01)]} renderOrder={0}>
          <planeGeometry args={[4.2, 6.2]} />
          <meshStandardMaterial 
            map={backTexture}
            side={THREE.DoubleSide}
            transparent={true}
            depthWrite={true}
            depthTest={true}
          />
        </mesh>
      </group>

      {/* Navigation buttons */}
      <Html position={[-1.5, -2, 0]}>
        <button 
          onClick={handlePrevPage}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            opacity: currentPage === 0 ? 0.5 : 1
          }}
          disabled={currentPage === 0}
        >
          Previous
        </button>
      </Html>
      <Html position={[1.5, -2, 0]}>
        <button 
          onClick={handleNextPage}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            opacity: currentPage === pages.length - 1 ? 0.5 : 1
          }}
          disabled={currentPage === pages.length - 1}
        >
          Next
        </button>
      </Html>
    </group>
  );
};

const ParticleSystem = () => {
  return (
    <Stars 
      radius={100}
      depth={50}
      count={5000}
      factor={4}
      saturation={0}
      fade
    />
  );
};

const PhotoAlbum = () => {
  return (
    <Frame
      boxShadow="in"
      style={{
        width: '60vh',
        height: '60vh',
        margin: '0 auto',
        overflow: 'hidden',
        border: '2px solid #000',
      }}
    >
      <Canvas style={{ width: '100%', height: '100%', background: 'black' }}>
        <OrbitControls />
        <Suspense fallback={null}>
          <ambientLight intensity={0.3} />
          <pointLight position={[10, 10, 10]} intensity={0.5} />
          <spotLight 
            position={[5, 10, 5]} 
            angle={0.3} 
            penumbra={1} 
            intensity={1} 
            castShadow 
          />
          <ParticleSystem />
          <Book />
        </Suspense>
      </Canvas>
    </Frame>
  );
};

export default PhotoAlbum; 